import { SeedInitiative } from './types';

export const SEED_INITIATIVE: SeedInitiative = {
  id: 'seed-americas-cloud-detection-uplift',
  name: 'Americas Cloud Detection Uplift — SIEM Consolidation',
  client: 'SMBC (illustrative)',
  champion: {
    name: 'Marcus Chen',
    title: 'VP, Technology Governance (Americas)',
    note: 'Internal requestor / GFD champion. Stemmler prepares this packet; Marcus enters it in Employee Center.',
  },
  consultantNotes:
    'Fictional but SMBC-shaped example so you can click the full champion flow. Yellow dropdowns are recommended with Confirm in ServiceNow. Use “Mark this option seen in ServiceNow” on a yellow value to train the playbook in this browser.',
  documents: [
    {
      id: 'one-pager',
      name: 'Americas Cloud Detection Uplift — one-pager (v0.4).docx',
      kind: 'Initiative one-pager',
      summary: 'Problem, outcome, owners, and why this is Capability Uplift / Cyber rather than a pure risk-reduction play.',
      excerpt: `Americas Cyber currently operates three partial detection stacks (legacy on-prem SIEM, a regional Splunk instance, and ad-hoc cloud-native logging in AWS/Azure). Coverage of identity and cloud-control-plane events is inconsistent. A 2025 Internal Audit non-regulatory finding (CYB-IA-2025-14, High) cited delayed detection of misconfigured cloud identities.

This initiative will (1) designate Microsoft Sentinel as the Americas cloud detection platform of record, (2) retain Splunk for on-prem and markets telemetry during a 12-month dual-run, (3) stand up a standard detection-engineering process (use-case intake → detect → tune → retire), and (4) reduce mean time to detect for cloud identity threats.

Primary driver is capability: we cannot operate a single detection operating model today. Risk reduction and some run-rate savings follow, but they are not the reason to do the work.`,
    },
    {
      id: 'cost-sheet',
      name: 'FY26 cost & headcount build (working).xlsx',
      kind: 'Cost workbook',
      summary: 'FY spend, remaining-lifecycle external cost, avoidance, inaction, FTEs, vendor mix.',
      excerpt: `Remaining lifecycle external cost (CapEx + vendor OpEx through go-live, excluding BAU run): ~$6.4M → band $5M–$10M.
Current FY26: $3.2M (Sentinel ingest + MSSP surge + 2 contractors + training).
Cost avoidance (3-year, duplicate Splunk cloud ingest + avoided emergency hire surge): $1.4M.
Cost of inaction (repeat audit finding + estimated incident-response overtime if cloud identity gap persists 18 months): $2.0M.
Net-new FTEs this FY: 4 (2 detection engineers Americas, 1 detection content lead, 1 cloud logging engineer). GCC content factory in later FY — not counted here.
Resourcing: Internal – Regional (Cyber + CISO office) AND External / Third-Party Resources (MSSP + Sentinel onboarding partner).
Timeline, approval to dual-run complete: 9 months → 6–12 Months.`,
    },
    {
      id: 'ciso-email',
      name: 'RE_ GF_ Cloud detection — sponsor confirmation.eml',
      kind: 'Sponsor email',
      summary: 'CISO Americas confirms sponsorship and names owner / COO execution lead.',
      excerpt: `From: James Okonkwo, CISO Americas
To: Priya Nair, Americas Cyber Lead; Elena Vasquez, COO Technology Operations

I will sponsor this as CxO. Priya owns delivery. Elena is COO execution lead for the operating-model cutover (follow-the-sun triage with Head Office SOC).

Please take this through Global Front Door this cycle. Head Office SOC is a beneficiary of the telemetry even though budget sits in Americas T4T Cyber. Treat regions as Americas + Head Office.

Do not frame this as Run the Bank / keep-the-lights-on. This is a new detection capability and a new cloud SIEM, with Splunk remaining as the existing plant.`,
    },
    {
      id: 'arch-notes',
      name: 'Working architecture notes — Sentinel dual-run.md',
      kind: 'Architecture notes',
      summary: 'Buy vs extend, systems touched, data types. Enough for Stage 1 change-type fields; C4L1 still belongs to the architect later.',
      excerpt: `Existing impacted application: Splunk (Americas enterprise SIEM) — remaining as on-prem/markets collector; integration and use-case migration.
New bank application: Microsoft Sentinel (cloud detection platform of record for Americas).
Will exchange data with IAM/PAM, AWS CloudTrail, Azure Activity, M365, and the Head Office SOC case system.
Personal information: security telemetry may include usernames and source IPs; not a customer-PII system of record.
Process: new detection-engineering intake and use-case lifecycle (capability/process enhancement).
People: SOC analysts and detection engineers need new playbooks and on-call (People Uplift is real but secondary — include it).
Discovery vs Execution: Discovery — target architecture and use-case backlog are not fully baselined; execution RFP comes after GFD.`,
    },
  ],
  fills: [
    {
      fieldId: 'initiative_type',
      value: 'Change the Bank - Strategic Transformation, New Product / Capability',
      extractionConfidence: 'high',
      sourceDocId: 'ciso-email',
      sourceSnippet: 'Do not frame this as Run the Bank / keep-the-lights-on. This is a new detection capability and a new cloud SIEM.',
      rationale: 'New platform + new operating model, not BAU maintenance. Live label may differ; pick the closest Change-the-Bank / new capability option.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'impacted_regions',
      value: ['Americas', 'Head Office'],
      extractionConfidence: 'high',
      sourceDocId: 'ciso-email',
      sourceSnippet: 'Treat regions as Americas + Head Office.',
      rationale: 'Budget is Americas; Head Office SOC consumes telemetry. Multi-region → global initiative.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'total_cost_band',
      value: '$5M–$10M',
      extractionConfidence: 'high',
      sourceDocId: 'cost-sheet',
      sourceSnippet: 'Remaining lifecycle external cost … ~$6.4M → band $5M–$10M.',
      rationale: 'Entire-lifecycle CapEx + project OpEx excluding run and sunk. Confirm the live band labels — training also called this remaining external cost.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'delivery_timeline',
      value: '6–12 Months',
      extractionConfidence: 'high',
      sourceDocId: 'cost-sheet',
      sourceSnippet: 'Timeline, approval to dual-run complete: 9 months → 6–12 Months.',
      rationale: 'Approval through dual-run complete, not just tooling install.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'supports_strategic_priority',
      value: 'Yes',
      extractionConfidence: 'high',
      sourceDocId: 'one-pager',
      sourceSnippet: 'Primary driver is capability: we cannot operate a single detection operating model today.',
      rationale: 'GFD key control. Always Yes for this packet.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'strategic_priority',
      value: 'Capability Uplift',
      extractionConfidence: 'high',
      sourceDocId: 'one-pager',
      sourceSnippet: 'Primary driver is capability… Risk reduction and some run-rate savings follow, but they are not the reason to do the work.',
      rationale: 'Primary driver is the detection operating model, not the audit finding alone.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'strategic_goal',
      value: 'Capability Uplift – Cyber',
      extractionConfidence: 'medium',
      sourceDocId: 'one-pager',
      sourceSnippet: 'why this is Capability Uplift / Cyber rather than a pure risk-reduction play',
      rationale: 'Only confirmed Cyber goal under Capability Uplift. If the live list has a more specific cyber goal, take that and report it back.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'requesting_portfolio',
      value: 'T4T - Cyber',
      extractionConfidence: 'high',
      sourceDocId: 'ciso-email',
      sourceSnippet: 'budget sits in Americas T4T Cyber',
      rationale: 'Sponsoring budget/ownership.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'beneficiary_portfolio',
      value: 'T4T - Cyber',
      extractionConfidence: 'medium',
      sourceDocId: 'ciso-email',
      sourceSnippet: 'Head Office SOC is a beneficiary of the telemetry even though budget sits in Americas T4T Cyber.',
      rationale: 'Greatest capability lift is still Cyber (Americas + HO SOC). Same value as requesting is valid.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'part_of_program',
      value: 'No',
      extractionConfidence: 'medium',
      sourceDocId: 'one-pager',
      sourceSnippet: 'This initiative will (1) designate Microsoft Sentinel as the Americas cloud detection platform of record',
      rationale: 'No named parent program in the packet. Training: No is fine for a new initiative.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'part_of_project',
      value: 'No',
      extractionConfidence: 'medium',
      sourceDocId: 'one-pager',
      sourceSnippet: 'This initiative will (1) designate Microsoft Sentinel',
      rationale: 'GFD record is the parent, not a child of an existing project.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'initiative_name',
      value: 'Americas Cloud Detection Uplift — SIEM Consolidation',
      extractionConfidence: 'high',
      sourceDocId: 'one-pager',
      sourceSnippet: 'Americas Cloud Detection Uplift',
      rationale: 'Short, distinguishable, matches how Cyber already names the work.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'problem_statement',
      value:
        'Americas Cyber cannot currently operate a single detection operating model for cloud. Telemetry is split across a legacy on-prem SIEM, a regional Splunk instance, and ad-hoc AWS/Azure logging, so identity and cloud-control-plane events are covered inconsistently and often late.\n\nInternal Audit non-regulatory finding CYB-IA-2025-14 (High) documented delayed detection of misconfigured cloud identities. Head Office SOC cannot treat Americas cloud telemetry as a reliable input to enterprise triage, which forces parallel, manual investigations.\n\nThe gap is not “we need another tool.” It is the absence of a platform of record, a use-case lifecycle (intake → detect → tune → retire), and a dual-run plan that does not strand Splunk on-prem/markets telemetry. If this remains unaddressed, the finding will repeat, MTTD on cloud identity threats stays high, and every new cloud workload adds another one-off logging pattern.',
      extractionConfidence: 'high',
      sourceDocId: 'one-pager',
      sourceSnippet: 'Coverage of identity and cloud-control-plane events is inconsistent. A 2025 Internal Audit non-regulatory finding (CYB-IA-2025-14, High) cited delayed detection of misconfigured cloud identities.',
      rationale: 'Written to survive Feasibility Fit. Do not shorten.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'intended_outcome',
      value:
        '1) Microsoft Sentinel is designated the Americas cloud detection platform of record, with Splunk retained for on-prem/markets telemetry during a documented 12-month dual-run.\n2) A standard detection-engineering process is live (use-case intake, detect, tune, retire) with a baselined Americas cloud use-case backlog.\n3) Mean time to detect for cloud identity / control-plane threats improves versus the CYB-IA-2025-14 baseline (target: 50% reduction within two quarters of dual-run start — exact KPI to be locked in Discovery).\n4) Head Office SOC can consume Americas cloud telemetry through the agreed case path without a second manual investigation stack.\n5) Net-new FY26 headcount (4 FTEs) and MSSP surge are mapped to named detection content and logging-pipeline work, not an open-ended body shop.',
      extractionConfidence: 'high',
      sourceDocId: 'one-pager',
      sourceSnippet: 'reduce mean time to detect for cloud identity threats',
      rationale: 'Measurable outcomes, not a restated slogan.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'initiative_owner',
      value: 'Priya Nair',
      extractionConfidence: 'high',
      sourceDocId: 'ciso-email',
      sourceSnippet: 'Priya owns delivery.',
      rationale: 'Search the user table for Priya Nair (Americas Cyber Lead). If the live directory uses a different display name, select that record.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'executive_sponsor',
      value: 'James Okonkwo',
      extractionConfidence: 'high',
      sourceDocId: 'ciso-email',
      sourceSnippet: 'I will sponsor this as CxO.',
      rationale: 'CISO Americas. Select the live user record.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'coo_execution_lead',
      value: 'Elena Vasquez',
      extractionConfidence: 'high',
      sourceDocId: 'ciso-email',
      sourceSnippet: 'Elena is COO execution lead for the operating-model cutover',
      rationale: 'COO Technology Operations. Select the live user record.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'change_type',
      value: [
        'Capability/Process Definition and/or Enhancement',
        'Change to an existing bank application',
        'Onboarding a new (bank) application',
        'People Uplift',
      ],
      extractionConfidence: 'high',
      sourceDocId: 'arch-notes',
      sourceSnippet: 'new detection-engineering intake… Splunk… Microsoft Sentinel… SOC analysts and detection engineers need new playbooks',
      rationale: 'All four categories apply. This is expected for an integration + new platform + operating-model change.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'existing_application',
      value: 'Splunk',
      extractionConfidence: 'high',
      sourceDocId: 'arch-notes',
      sourceSnippet: 'Existing impacted application: Splunk (Americas enterprise SIEM)',
      rationale: 'Search EAI for Splunk. If the inventory name is longer (e.g. Splunk Enterprise SIEM — Americas), pick that record.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'new_application_name',
      value: 'Microsoft Sentinel',
      extractionConfidence: 'high',
      sourceDocId: 'arch-notes',
      sourceSnippet: 'New bank application: Microsoft Sentinel (cloud detection platform of record for Americas).',
      rationale: 'Net-new platform of record. Confirm whether the live field is free text or another EAI lookup.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'discovery_or_execution',
      value: 'Discovery',
      extractionConfidence: 'high',
      sourceDocId: 'arch-notes',
      sourceSnippet: 'Discovery vs Execution: Discovery — target architecture and use-case backlog are not fully baselined',
      rationale: 'Architecture and backlog not locked; execution RFP follows GFD.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'assumptions',
      value: 'Some Assumptions – mostly based on reasonable expectations and require validation',
      extractionConfidence: 'medium',
      sourceDocId: 'arch-notes',
      sourceSnippet: 'target architecture and use-case backlog are not fully baselined; execution RFP comes after GFD',
      rationale: 'Roughly 10–19: Sentinel ingest volumes, dual-run duration, MSSP capacity, HO case-system interface, exact MTTD baseline. Not 20+.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'dependencies',
      value: 'Some Important Dependencies – could hinder progress',
      extractionConfidence: 'medium',
      sourceDocId: 'arch-notes',
      sourceSnippet: 'Will exchange data with IAM/PAM, AWS CloudTrail, Azure Activity, M365, and the Head Office SOC case system.',
      rationale: 'IAM, cloud landing zones, HO SOC case system, Splunk retention — important but countable, not 20+ blockers.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'additional_headcount',
      value: 4,
      extractionConfidence: 'high',
      sourceDocId: 'cost-sheet',
      sourceSnippet: 'Net-new FTEs this FY: 4 (2 detection engineers Americas, 1 detection content lead, 1 cloud logging engineer).',
      rationale: 'Current FY only. GCC factory is later FY — leave it out.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'resourcing_source',
      value: ['Internal – Regional', 'External / Third-Party Resources'],
      extractionConfidence: 'high',
      sourceDocId: 'cost-sheet',
      sourceSnippet: 'Internal – Regional (Cyber + CISO office) AND External / Third-Party Resources',
      rationale: 'No GCC this FY. Confirm live checkbox wording.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'fy_cost',
      value: 3200000,
      extractionConfidence: 'high',
      sourceDocId: 'cost-sheet',
      sourceSnippet: 'Current FY26: $3.2M',
      rationale: 'Enter 3200000 or 3,200,000 depending on how the currency field is masked.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'cost_avoidance',
      value: 1400000,
      extractionConfidence: 'medium',
      sourceDocId: 'cost-sheet',
      sourceSnippet: 'Cost avoidance (3-year…): $1.4M.',
      rationale: 'Avoided duplicate ingest + emergency hiring. Enter 1400000.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'cost_of_inaction',
      value: 2000000,
      extractionConfidence: 'medium',
      sourceDocId: 'cost-sheet',
      sourceSnippet: 'Cost of inaction … $2.0M.',
      rationale: 'Repeat finding + IR overtime. Enter 2000000.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'issue_alignment',
      value: 'High Rated Issues (Non-Regulatory Findings)',
      extractionConfidence: 'high',
      sourceDocId: 'one-pager',
      sourceSnippet: 'Internal Audit non-regulatory finding (CYB-IA-2025-14, High)',
      rationale: 'Matches the training’s selected example wording. Confirm punctuation (High-Rated vs High Rated).',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'issue_details',
      value: 'Issue ID: CYB-IA-2025-14\nType: Internal Audit — cloud identity logging / detection coverage\nRating: High (non-regulatory)\nDue date: confirm in Makoto / issue system before submit if the field is required',
      extractionConfidence: 'medium',
      sourceDocId: 'one-pager',
      sourceSnippet: 'CYB-IA-2025-14, High',
      rationale: 'Paste into whatever follow-up boxes appear. If the live form stays on one dropdown, skip extra boxes.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'capability_uplift_level',
      value: 'Impacts an L1 capability',
      extractionConfidence: 'medium',
      sourceDocId: 'one-pager',
      sourceSnippet: 'Primary driver is capability: we cannot operate a single detection operating model today.',
      rationale: 'Touches Cyber L1 (Detect / Protect), not a single minor L2 tweak.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'impacted_capabilities',
      value: 'Cyber — Detect; Cyber — Protect (search live table; “Cyber Security Management” appeared in training and may still be an umbrella label)',
      extractionConfidence: 'medium',
      sourceDocId: 'arch-notes',
      sourceSnippet: 'cloud detection platform of record',
      rationale: 'Search Detect and Protect first. If the inventory still uses “Cyber Security Management,” select that and report it — that is a known label mismatch.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'runrate_savings',
      value: '$1M–$5M',
      extractionConfidence: 'medium',
      sourceDocId: 'cost-sheet',
      sourceSnippet: 'Cost avoidance (3-year…): $1.4M.',
      rationale: '3-year run-rate is in the $1–5M band if the live list matches training. Do not use the $1.4M currency field here — this control is banded.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'revenue_growth',
      value: '<$10M (supports a function that eventually touches revenue)',
      extractionConfidence: 'medium',
      sourceDocId: 'one-pager',
      sourceSnippet: 'Risk reduction and some run-rate savings follow, but they are not the reason to do the work.',
      rationale: 'Security capability; only indirect revenue. Training’s selected wording is the honest fit.',
      confirmInServiceNow: true,
    },
    {
      fieldId: 'additional_comments',
      value: '',
      extractionConfidence: 'low',
      sourceDocId: '',
      sourceSnippet: '',
      rationale: 'Optional Activity-tab note — leave blank unless something did not fit a field.',
      confirmInServiceNow: false,
    },
    {
      fieldId: 'submit_action',
      value: 'Click Submit — then send IDEA####### back to the consultant. Attach the one-pager and cost sheet on My Request.',
      extractionConfidence: 'high',
      sourceDocId: 'one-pager',
      sourceSnippet: 'Please take this through Global Front Door this cycle.',
      rationale: 'No Save as Draft. Attachments can be added after submit.',
      confirmInServiceNow: false,
    },
  ],
};
